import React, { FunctionComponent, useState, useEffect } from 'react';

import { forEach, map } from 'lodash';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { InputSearchDropdown, OPTIONAL_DATA_KEY } from 'common/input-search-dropdown/InputSearchDropdown';
import 'core/list-builder/ListBuilder.scss';

export interface InputItem {
    id: any;
    placeholder?: string;
}

interface ListItem {
    [key: string]: any;
}

export interface Props {
    /** meta-data to render input */
    inputMetadata?: InputItem[];
    /** list items to be displayed on load (edit mode) */
    defaultList?: ListItem[];
    /** ability to toggle no-data label display */
    displayNoDataLabel?: boolean;
    /** label for no-data, needs displayNoDataLabel: true, default: 'No Data' */
    noDataLabel?: string;
    /** ability to view list items in-line */
    displayAsBubbles?: boolean;
    /** callback when list-item is added or removed, returns updated list */
    onChange?: any;
    /** api callback that uses the entered text as input and returns a promise */
    getData?: (inputValue: string) => Promise<OptionData[]>;
    /** denotes if the output list has custom JSX elements */
    DropdownMenuCustomComponent?: FunctionComponent<any>;
    /** invalid feedback string */
    invalidText?: string;
}

export interface OptionData {
    /** text to be displayed in the search dropdown */
    displayName: string;
    [OPTIONAL_DATA_KEY]?: any;
}

export const ListBuilder: FunctionComponent<Props> = ({
    inputMetadata,
    defaultList,
    displayNoDataLabel,
    noDataLabel,
    displayAsBubbles,
    onChange,
    getData,
    DropdownMenuCustomComponent,
    invalidText,
}): JSX.Element => {
    const { t } = useTranslation(['core']);
    const [list, setList] = useState<ListItem[]>([]);
    const [inputValues, setInputValues] = useState<string[]>([]);
    /** Keeps track of currently selected elements */
    const [selectedOption, setSelectedOption] = useState<any>({});
    const [inputInvalidText, setInputInvalidText] = useState<string>(invalidText || '');

    useEffect(() => {
        setList(defaultList || []);
    }, [defaultList]);

    useEffect(() => {
        setInputInvalidText(invalidText || '');
    }, [invalidText]);

    const addToList = (): void => {
        const listItem: any = {};
        const newList = [...list];

        setInputInvalidText('');

        forEach(inputMetadata, (inputItem, inputIndex) => {
            if (inputValues[inputIndex]) {
                listItem[inputItem.id] = inputValues[inputIndex];
                inputValues[inputIndex] = '';
                /** If getData is present then use the custom element and data and reset the listItem */
                if (getData && selectedOption[inputIndex]) {
                    listItem[OPTIONAL_DATA_KEY] = selectedOption[inputIndex];
                }
            }
        });

        // if (listItem.key && listItem.value) { // reverted changes of cred-590
        if (Object.keys(listItem).length) {
            newList.push(listItem);
            setSelectedOption({});

            if (onChange) {
                onChange(newList);
            }
        }
        // }
    };

    const removeFromList = (index: number): void => {
        const newList = [...list];

        newList.splice(index, 1);
        setList(newList);
        if (onChange) {
            onChange(newList);
        }
    };

    const getListItems = (listItem: any, listItemIndex: number): JSX.Element => {
        const values: any = [];
        let itemCount = 0;
        const noOptData = { ...listItem };

        delete noOptData[OPTIONAL_DATA_KEY];

        forEach(listItem, (itemData, keyName) => {
            if (keyName === OPTIONAL_DATA_KEY) return;
            itemCount += 1;
            values.push(
                <span key={`${keyName}-${listItemIndex}`} className="value">
                    {DropdownMenuCustomComponent ? (
                        <DropdownMenuCustomComponent additionalData={listItem} key={`${keyName}-${listItemIndex}`} />
                    ) : (
                        itemData
                    )}
                    {displayAsBubbles &&
                    Object.keys(noOptData).length > 1 &&
                    itemCount !== Object.keys(noOptData).length
                        ? ': '
                        : ''}
                </span>,
            );
        });

        return (
            <div key={listItemIndex} className="list-item d-flex align-items-center">
                {values}
                <i
                    className="icon-times"
                    role="button"
                    aria-label="Remove list item"
                    onClick={(): void => {
                        removeFromList(listItemIndex);
                    }}
                    onKeyPress={(): void => {
                        // Do Nothing
                    }}
                    tabIndex={0}
                />
            </div>
        );
    };

    return (
        <>
            <div className={`r4e-list-builder h-100 ${inputInvalidText && 'r4e-list-builder-invalid'}`}>
                <div className="list-input-container h-100">
                    <div className="input-container h-100">
                        {map(
                            inputMetadata,
                            (item, index): JSX.Element => (
                                <InputSearchDropdown
                                    key={`input-search-dropdown-${index}`}
                                    placeholder={item.placeholder}
                                    getDropdownData={getData}
                                    value={inputValues[index]}
                                    DropdownMenuCustomComponent={DropdownMenuCustomComponent}
                                    onDropdownOptionSelect={(option: OptionData): void => {
                                        const newInputValues = [...inputValues];

                                        newInputValues[index] = option.displayName;
                                        setInputValues(newInputValues);
                                        setSelectedOption({
                                            ...selectedOption,
                                            [index]: option[OPTIONAL_DATA_KEY],
                                        });
                                    }}
                                    onChange={(input: string): void => {
                                        const newInputValues = [...inputValues];

                                        newInputValues[index] = input;
                                        setInputValues(newInputValues);
                                    }}
                                />
                            ),
                        )}
                    </div>
                    <div className="action-container">
                        <Button variant="primary" onClick={addToList}>
                            <i className="icon-plus" />
                        </Button>
                    </div>
                </div>
                <div>
                    {!list || !list.length ? (
                        <>{displayNoDataLabel ? <div className="list-no-data">{t(noDataLabel!)}</div> : null}</>
                    ) : (
                        <div
                            className={`
                    ${displayNoDataLabel || (list && list.length) ? 'list-data-container' : ''} 
                    ${displayAsBubbles ? 'bubble no-border' : 'block'}`}
                        >
                            {map(list, (listItem, listItemIndex) => {
                                return getListItems(listItem, listItemIndex);
                            })}
                        </div>
                    )}
                </div>
            </div>
            {inputInvalidText ? (
                <span className="r4e-list-builder-invalid-text text-danger">
                    {inputInvalidText || t('VALIDATION_LIST_BUILDER')}
                </span>
            ) : null}
        </>
    );
};

ListBuilder.defaultProps = {
    inputMetadata: [
        {
            id: 'value',
            placeholder: 'VALUE',
        },
    ],
    noDataLabel: 'NO_DATA',
    displayNoDataLabel: true,
    displayAsBubbles: false,
    defaultList: [],
};
